import { Colors } from '../../ui/colors'
import {
  fitFontPx,
  LCD_DIM,
  LCD_FONT,
  LCD_GREEN,
  LCD_INK,
  LCD_RED,
  LED_AMBER,
  LED_FONT,
  LED_GREEN,
  LED_RAIL,
  LED_RED,
  LED_SCANLINES,
  ledGlow,
} from '../../ui/theme'
import type { VehicleType } from '../../store/transit'
import {
  formatAbsoluteClock,
  formatCountdown as formatTransitCountdown,
  formatShortClock,
} from '../../ui/transitTime'

// Shared chrome for the rider panel's two faces (PlanPrompt before boarding, RidingPanel
// after). A plain module because a `.svelte` component can't export constants.

/**
 * The on-board panel: a drawer rising from the BOTTOM of the screen, opaque black with an 18px
 * top radius and the one piece of chrome unique to this screen — a `#23262e` top edge, the
 * rollsign's bezel. The only opaque sheet in the app: the phosphor glow has to read against a
 * true black, and a translucent panel would let moving vehicles bleed through the lit type.
 *
 * ⚠️ It hangs from the BOTTOM, and it hung from the top for as long as it existed — so a
 * comment anywhere in this folder that says otherwise is stale, not a second opinion. The
 * bottom is the edge a phone is held at and the edge every other sheet in the app opens from,
 * which puts the next stop under the rider's thumb instead of at arm's length, and it is where
 * both faces now sit: the prompt and the sign it turns into share one edge, so nothing jumps
 * when the rider boards.
 *
 * What that costs is the edge itself, which the tab sheets and the map's detail sheets already
 * own, and the Z-INDEX is the whole resolution: 150 is above the map and its floating controls
 * (50–100) and below every sheet — tab sheets 201, NearbyStations 200, the detail sheets 1000,
 * MapSheet 2000. So an OPEN SHEET COVERS THE DRAWER and the drawer comes back when it closes.
 * That is the trade a bottom anchor forces: a rider looking a line up in Alerts gets the whole
 * sheet rather than the ~70px of it a drawer stacked on top would leave, and loses the next
 * stop while they read. Raising this above 201 does not buy the next stop back — it buys a
 * sheet with a black panel over its lower two thirds.
 *
 * ⚠️ NEITHER FACE PADS FOR THE SAFE AREA, and both used to pad with `safeTop`. The drawer's
 * containing block is `App`'s page-content box, whose bottom edge is the TOP OF THE TAB BAR,
 * and the bar has already spent `safeBottomButtons` on the navigation inset. A `safeBottom`
 * here would be that inset counted twice, as a band of black above the bar.
 */
export const panelShell =
  'position: absolute; bottom: 0; left: 0; right: 0;' +
  ` background-color: ${Colors.ground};` +
  ' border-top: 2px solid #23262e;' +
  ' border-top-left-radius: 18px;' +
  ' border-top-right-radius: 18px;' +
  ' z-index: 150;'

/**
 * Clear air along the drawer's BOTTOM edge, which is now the edge that meets the tab bar.
 *
 * It exists because the drag handle used to live down there, and its own 6px of padding was
 * what kept the control row off the drawer's rim. The handle moved to the top with the anchor
 * (see `panelShell`), and both control rows — the train's and `BusStopScreen`'s footer, each
 * `padding: 4px … 0` and each pinned down by `margin-top: auto` — would otherwise sit flat on
 * the tab bar's own hairline border. One constant on the drawer rather than two on the rows,
 * so the two faces cannot end up with different gutters, and the mini face gets it for free.
 *
 * ⚠️ It is `box-sizing: border-box` padding, so it comes OUT of the snap height. `SNAP_DEFAULT`
 * is 6 taller than it was for exactly this reason.
 */
export const PANEL_BOTTOM_GUTTER = 6

/**
 * The drawer's TOP rail — the drag handle, and nothing else: a 4px pill in 6px of padding
 * either side.
 *
 * ⚠️ The ✕ spent a version up here and came back down. The rail is the one band of the drawer
 * that says nothing, so a control in it reads as chrome on the drawer rather than part of the
 * ride, and it sits a whole row above the line a rider is actually looking at. It is on the
 * ROUTE line now — the same line as the bullet and where the vehicle is going — on all three
 * faces: the sign band on the train (`DestinationSign`'s `trailing`), the headline row on the
 * bus, and the one row the mini face has. That is the line every face has, which is what lets
 * the target stay put as the drawer changes height, and holding the ✕ costs it no height at
 * all: the bullet is 30px on the train band and the button's box is 28.
 */
export const PANEL_TOP_RAIL_H = 16

// Three snap heights. DEFAULT is dictated by the rollsign, not chosen: the strip's diagonal
// labels need `ROLLSIGN_STRIP_H` (below). Anything shorter clips stop names at an angle,
// which is worse than not showing them — so the default IS the sign, and MINI is the way
// out of it.
export const SNAP_MINI = 72
// Two bands and the strip between them, and nothing else: the sign band (~46), the rollsign
// (280), the control row (~41), the top rail (`PANEL_TOP_RAIL_H`, 16) and
// `PANEL_BOTTOM_GUTTER` (6). 455 was what this cost when the destination, the countdown and
// the bullet were three stacked rows; they are one band now.
//
// It was 388 — the gutter is the 6 the drawer's bottom edge lost when the handle moved to the
// top of it, and this is where that has to be paid: the five figures above are each holding
// something up, and taking the gutter out of the 388 instead would overflow the face box by a
// px and hand the rim back what it was bought to keep clear. The bus's 455 has the slack and
// keeps it.
//
// ⚠️ It briefly went to 406, when the ✕ sat in the top rail and made it 28. Putting that
// button on the ROUTE line instead costs nothing: the band it joins is already 30px tall
// because the bullet is. A control that needs a row of its own is the thing to be suspicious
// of here — this panel's height is the rollsign's, and everything else is paying rent.
//
// The control row holds everything else — crowding and the 3D toggle — with room now: 352px
// on a 360px phone against ~290 of content, since the ✕ left for the sign band. The slack
// goes to crowding, the row's only flexible block. The band itself cannot move down into that
// row; see `STRIP_MIN_W`'s comment for the arithmetic.
//
// The panel overflows hidden, so anything that wants a row of its own — the upcoming
// transfer, End trip — still only appears once the rider has dragged past DEFAULT.
export const SNAP_DEFAULT = 394
/**
 * The bus keeps the height the panel had before the split, because the split is a SUBWAY
 * layout: an R160 front is two displays side by side, the LCD over a farebox is one screen,
 * and `BusStopScreen` is a vertical list whose usefulness is simply how many stops it can
 * show. At 340 it shows the next stop and one more. Nothing was bought by shortening it —
 * the destination rows it would have to move sideways have nowhere to go.
 */
export const SNAP_DEFAULT_BUS = 455
// Never below either default: on a short viewport 72% can be less than the sign needs, and a
// "full" snap smaller than a default would collapse the drag range to a point.
export const SNAP_FULL = Math.max(
  SNAP_DEFAULT_BUS,
  Math.round(typeof window !== 'undefined' ? window.innerHeight * 0.72 : 560),
)

/**
 * Nearest snap point to a dragged height. `defaultH` is the middle stop, which differs per
 * face — pass the one the panel is currently wearing, or a bus drag lands on the train's.
 */
export function nearestSnap(h: number, defaultH: number = SNAP_DEFAULT): number {
  return [SNAP_MINI, defaultH, SNAP_FULL].reduce((a, b) =>
    Math.abs(b - h) < Math.abs(a - h) ? b : a,
  )
}

/** How close a train has to be for the one-tap board button to offer it. */
export const BOARD_RADIUS_M = 400

// ---------------------------------------------------------------------------
// The sign band, and what the strip is left with
// ---------------------------------------------------------------------------
// An R160 car front carries TWO displays side by side, and the panel reproduced that: an LCD
// column (service name over terminus, bullet in the corner) beside the amber dot-matrix
// rollsign. It is the more faithful arrangement and it is not the one here, because a phone
// is not a car front. Beside the strip, the LCD could only ever have 104–148px — too narrow
// to be the landscape display it is imitating, and every px of it came out of the strip,
// whose diagonal callout is the thing that actually has to fit. The sign is a full-width
// BAND above the strip now: bullet, then service name over destination, then the one line
// with a deadline.
//
// It costs ~46px of drawer height and buys the strip 112px of width, which is the trade
// worth making — the callout's name box goes from 166px to 249, and station names that used
// to bottom out at the 12px floor and ellipsise ("Atlantic Av-Barclays Ctr") now set at 18.
//
// It is NOT in the control row below it. That was asked for and does not fit: the crowding
// control and the 3D toggle spend ~290 of the 352px a 360px phone has, and the bullet, name
// and destination want ~300 more. Two rows is the fewest this fits in — the ✕ leaving that
// row bought ~53px, which is a third of what the band needs. The traffic went the other way:
// the ✕ now rides the BAND (`DestinationSign`'s `trailing`), which had the room, since a
// notice is the only other thing that ever lands at its right end.
/**
 * The panel's ONE side gutter — the sign band, the strip and the control row all read it, so
 * the three line up down the left edge and nothing sits closer to the screen than anything
 * else. The control row used to keep its own 4px, from when it was tight enough to need it.
 *
 * It is the input to `STRIP_MIN_W`, so widening it is spending the callout's width: the bar's
 * horizontal limit falls ~1.2px per px added. There is room today because the callout's
 * VERTICAL limit is the binding one (273 against 314), and this may grow until the two meet.
 * The control row is the tighter constraint in practice — at 20 the crowding label starts to
 * ellipsise.
 */
export const STRIP_PAD_X = 16
/** The narrowest viewport this is laid out for — a 360px phone, the small end still sold. */
export const SIGN_MIN_VIEWPORT_W = 360
/**
 * What the strip is GUARANTEED, and so the width the next-stop callout must fit inside — now
 * simply the viewport less its own padding, because nothing sits beside it any more.
 * `shell.test.ts` asserts the bar against it.
 */
export const STRIP_MIN_W = SIGN_MIN_VIEWPORT_W - 2 * STRIP_PAD_X

// The band's own type. No fitting math: at 336px wide the longest service name in the table
// ("Brooklyn–Queens Crosstown") is 132px and the longest station name on the system
// ("Sutphin Blvd-Archer Av-JFK Airport") is 224, so both sit on one line at a fixed size and
// the ellipsis is a backstop rather than a mechanism. The column this replaced needed three
// fitting helpers to survive 104px; none of them survived the move, which is the point.
/** The bullet sets the band's height — it is the tallest thing in it, as on the real panel. */
export const SIGN_ROW_BULLET_PX = 30
/** Secondary: the bullet already says which line this is. */
export const SIGN_ROW_NAME_PX = 12
/** The band's real payload, and the only thing in it a rider is looking for. */
export const SIGN_ROW_DEST_PX = 15

// The panel's headline shrinks to fit rather than clipping, and so does every route bullet in
// the app — one measurement routine serves both, and it lives in `ui/theme` beside the bullet
// helpers that call it. Re-exported here because the rider panel is its other caller.
export { fitFontPx }

// ---------------------------------------------------------------------------
// The rollsign strip's diagonal stop labels
// ---------------------------------------------------------------------------
// A stop name on the rollsign hangs off the rail at an angle, so its LENGTH is spent going
// UP: every extra character pushes the far end of the label closer to the top of the strip,
// which clips (`overflow: hidden`). That is how "Atlantic Av-Barclays Ctr" lost its "Ctr"
// and "Franklin Av-Medgar Evers College" stopped at "Ever". So the room a label has is a
// VERTICAL budget expressed as a diagonal run — NOT the column width, which is only 96px
// and which every label overhangs by design.
//
// That angle is 35°, measured off a photograph of the real sign (the working is at
// NEXT_ANGLE below), and it is ONE angle for the whole face: the next-stop bar, the name
// inside it and every upcoming stop's label all lie on it, so the strip reads as a set of
// parallel rules rather than a fan — the tick marks included (see ROLLSIGN_TICK_ROTATE).
//
// Two things get better as the angle shallows and one gets worse, and the worse one is the
// binding constraint. Better: the vertical budget below buys a longer run (282px at 35°
// against 206px at 52°), so far fewer names shrink. Worse: neighbouring labels are parallel
// lines whose perpendicular separation is ROLLSIGN_COL_W · sin(angle) — 55px here, against
// 76px at 52° — and a whole label block, name over transfer row, has to fit inside that with
// air to spare. That separation is one of the two budgets the names are SIZED from
// (`NAME_MAX_PX`, under the callout, since the callout's bar is the other); `shell.test.ts`
// asserts the air. Shallowing further, or narrowing the columns, spends it.

/** Height of the scrolling strip the labels are clipped by. */
export const ROLLSIGN_STRIP_H = 280
export const ROLLSIGN_LABEL_ANGLE = 35
/** One upcoming-stop column. The next-stop callout has its own, wider, column. */
export const ROLLSIGN_COL_W = 96
/**
 * Where a label starts, measured down from the top of the strip. The label block is anchored
 * by its TOP, not its bottom: it rotates about its top-left corner, so a bottom-anchored
 * block moves its own pivot whenever its height changes — a stop with a transfer row would
 * start higher than one without, and the fitted size below would depend on its own result.
 *
 * This is 165 and not the 172 it was: 165 is what the column actually renders at, so the
 * budget below was handing the longest names 12px of run that did not exist and they spent
 * it out of the gutter. The number now has one home and the markup reads it.
 */
export const ROLLSIGN_LABEL_TOP = 165
/** And where it starts across the column — clear of the tick that points up at it. */
export const ROLLSIGN_LABEL_LEFT = 54
/** Clear air kept between the longest label and the header bar above the strip. */
const LABEL_TOP_GUTTER = 10

/**
 * How far a label may run from its pivot before it reaches the top of the strip. Floored:
 * it is also written into a `max-width`, and a whole px is both conservative and readable.
 */
export const ROLLSIGN_LABEL_RUN_PX = Math.floor(
  (ROLLSIGN_LABEL_TOP - LABEL_TOP_GUTTER) /
    Math.sin((ROLLSIGN_LABEL_ANGLE * Math.PI) / 180),
)

const rad = (deg: number) => (deg * Math.PI) / 180

// ---------------------------------------------------------------------------
// The rail and its tick marks
// ---------------------------------------------------------------------------
// The rail is the horizontal rule the stops hang off, and a tick is the short diagonal
// joining one stop to it. A tick is on the LABELS' angle — fitting the photo's ticks gives
// 33° against the names' 35°, one measurement apart, and they read as one stroke: the tick
// points up the line the name is set on. It is the only thing on the face that could have
// had an angle of its own, and it does not.
export const ROLLSIGN_RAIL_BOTTOM = 58
export const ROLLSIGN_RAIL_H = 3
/** A tick's length along its own diagonal, and where its foot meets the rail in a column. */
export const ROLLSIGN_TICK_LEN = 34
export const ROLLSIGN_TICK_X = 20
/**
 * The tick is authored as an upright bar and rotated, so its rotation is measured off
 * VERTICAL — the complement of the angle everything else on the face is measured by.
 */
export const ROLLSIGN_TICK_ROTATE = 90 - ROLLSIGN_LABEL_ANGLE

// The rail does not run off the left edge of the strip. The rider is at the head of the
// line, so the line ENDS there: on the sign it turns up through a short hook and stops with
// a round cap. In the photo that hook rises 11px while reaching 7px back on a 6px rail, and
// a circular turn with that rise and reach is cot(θ/2) = 7/11 ⇒ θ = 115°, on a radius of
// ~1.3 rail-thicknesses. Ours is a wider radius against a thinner rail (6px on 3px): at the
// sign's own ratio the whole hook would be 4px tall on a 280px strip, which is a nick in the
// rail rather than a terminus.
export const ROLLSIGN_HOOK_R = 6
export const ROLLSIGN_HOOK_TURN = 115
/** Where the rail's straight part begins — the hook is the 5px to the left of this. */
export const ROLLSIGN_HOOK_X = 36
export const ROLLSIGN_HOOK_RISE = ROLLSIGN_HOOK_R * (1 - Math.cos(rad(ROLLSIGN_HOOK_TURN)))
export const ROLLSIGN_HOOK_BACK = ROLLSIGN_HOOK_R * Math.sin(rad(ROLLSIGN_HOOK_TURN))
/** Tall enough for the hook and both round caps; the rail sits along its bottom edge. */
export const ROLLSIGN_RAIL_SVG_H = Math.ceil(ROLLSIGN_HOOK_RISE + ROLLSIGN_RAIL_H * 2)

/**
 * The next-stop column's rail, as an SVG path: in from the right, then up and done.
 *
 * The arc's centre is directly above the point the straight run ends at, which is what makes
 * the rail leave the hook horizontally instead of kinking into it — hence sweep-flag 1, the
 * clockwise arc, which is the one whose centre lands there. The other choice curls the hook
 * DOWN through the rail.
 */
export function rollsignRailPath(width: number): string {
  const cy = ROLLSIGN_RAIL_SVG_H - ROLLSIGN_RAIL_H / 2
  const endX = (ROLLSIGN_HOOK_X - ROLLSIGN_HOOK_BACK).toFixed(2)
  const endY = (cy - ROLLSIGN_HOOK_RISE).toFixed(2)
  return (
    `M ${width} ${cy} L ${ROLLSIGN_HOOK_X} ${cy} ` +
    `A ${ROLLSIGN_HOOK_R} ${ROLLSIGN_HOOK_R} 0 0 1 ${endX} ${endY}`
  )
}

// ---------------------------------------------------------------------------
// The next-stop callout: the red bar and the "Next Stop" label beside it
// ---------------------------------------------------------------------------
// These are measured off a photograph of the real sign, not chosen. Fitting the red
// outline's long edges in that photo puts them at 35° — and the sign's bottom rail, which
// is level in the world, photographs at 0.5°, so there is no camera roll inflating that
// number: 35° is the sign's own angle. The bar is a LONG THIN outline (411 × 73 px in the
// photo, ~6.7:1 on the lit centrelines) whose first stretch holds the station name and
// whose tail is empty — a fixed frame the name is set into, not a box grown to the name.
// "Next Stop" begins at the SAME point along the diagonal as the bar (within 3px in the
// photo), runs 0.72 of its length, stands 0.33 bar-heights clear of its upper edge, and is
// set at 0.64 of the bar's height.
//
// Everything else here follows from those ratios and TWO hard limits, one per axis.
//
// Everything is anchored at the bar's bottom-left corner and every other point on the
// callout is that corner plus a rotation, so ALL FOUR corners of a rotated box have to be
// checked — not the anchored one. Getting that wrong is what sheared the "N" off "Next Stop"
// for as long as the split has existed: see `NEXT_LABEL_REACH_LEFT`.
//
// Three edges bind, and each one now DERIVES the constant that answers it rather than being
// checked against a number someone wrote down:
//
//   left    the label's top-left corner must clear x = 0, since the strip's scroll clip
//           cannot be scrolled back past its own origin  →  NEXT_BOX_LEFT
//   right   the bar's far corner must stay inside `STRIP_MIN_W`, what the split leaves the
//           strip on a 360px phone; a red frame severed by that clip reads as a rendering
//           bug rather than as the format  →  NEXT_BOX_W
//   below   column 2's stop name must stay clear of the bar's lower edge — parallel lines,
//           so one clearance holds for the whole length  →  NEXT_COL_W
//
// Vertically the callout is clipped by the same `ROLLSIGN_STRIP_H` the labels are, and both
// the bar and the label top out well under it (~266 and ~232 of 280); it is the only axis
// with room to spare, which is why nothing is derived from it. `shell.test.ts` asserts all
// four, so widening the sign panel beside the strip is what makes them fail.
//
// The 35° is the whole face's angle, not the callout's own — see ROLLSIGN_LABEL_ANGLE,
// which every upcoming stop's label is set on too. Named here as well because this is
// where it was measured, and because the arithmetic above is unreadable without it.
export const NEXT_ANGLE = ROLLSIGN_LABEL_ANGLE
/**
 * Where the bar's bottom-left corner sits above the strip floor: 13px clear of the rail,
 * over the hook that ends it. Moving it moves the whole callout and re-opens the fit above.
 */
export const NEXT_BOX_BOTTOM = 74
/** Fixed: an empty tail is the format, so the frame must not track the name's height. */
export const NEXT_BOX_H = 56
export const NEXT_BOX_BORDER = 4
export const NEXT_BOX_PAD_X = 8
/** Perpendicular clear air between the bar's upper edge and the "Next Stop" label. */
export const NEXT_LABEL_GAP = 18
/** 0.64 of the bar's height, which is the ratio the photo gives. */
export const NEXT_LABEL_PX = 36
/**
 * A line box is a hair taller than its font size once the face's own metrics are in it —
 * measured at 1.036 for VT323 at 36px. Rounded up, because this feeds a clearance.
 */
const NEXT_LABEL_LINE_H = 1.05

/**
 * How far LEFT of the bar's pivot the "Next Stop" label reaches.
 *
 * ⚠️ This is the label's TOP-left corner, not its bottom-left one, and the difference is the
 * whole point. The label hangs `NEXT_BOX_H + NEXT_LABEL_GAP` perpendicular below-left of the
 * pivot and is then rotated about its own bottom-left corner — so its own HEIGHT carries the
 * top corner a further `h·sin35` left again, 22px at 36px type. Measuring only the bottom
 * corner said the label started at x = 7.6 and cleared the strip; it actually started at
 * −13.8 and the "N" was sheared off by the strip's own scroll clip, which is not scrollable
 * past its own origin. Both corners are checked in `shell.test.ts` now.
 */
export const NEXT_LABEL_REACH_LEFT =
  (NEXT_BOX_H + NEXT_LABEL_GAP + NEXT_LABEL_PX * NEXT_LABEL_LINE_H) * Math.sin(rad(NEXT_ANGLE))
/**
 * Lit marks bloom — the label through `ledGlow`, the bar through a 14px `box-shadow` — so
 * both ends want a little air and not just clearance, or the glow is what gets sheared by
 * the strip's clip instead of the mark. Measured flush at 0: the bar's far corner landed
 * exactly on the strip's right edge and its halo stopped dead there.
 */
const NEXT_EDGE_GUTTER = 3
/**
 * Derived, not chosen: it is exactly what the label needs to clear the strip's left edge.
 * Everything downstream of it — the bar's length, the callout column's width — is then a
 * consequence, which is why those two are computed here rather than written down.
 */
export const NEXT_BOX_LEFT = Math.ceil(NEXT_LABEL_REACH_LEFT) + NEXT_EDGE_GUTTER
/**
 * As long as the strip can hold, and the strip now binds on the OTHER axis.
 *
 * A diagonal bar spends every px of length on both axes at once, so lengthening it walks the
 * far corner right AND up. With the sign band moved off the strip's side there are 336px of
 * width, which would allow 323 — but at 323 the bar's high corner reaches y = 305 in a 280px
 * strip and the top of the frame is sheared off instead of its tail. Both limits are written
 * out and the smaller wins; on today's numbers that is the vertical one, at 273 against 323.
 * Neither may be dropped: the split's 224px strip made the horizontal one bind, and a phone
 * narrower than `SIGN_MIN_VIEWPORT_W` would again.
 */
const NEXT_BOX_W_BY_WIDTH =
  (STRIP_MIN_W - NEXT_EDGE_GUTTER - NEXT_BOX_LEFT) / Math.cos(rad(NEXT_ANGLE))
const NEXT_BOX_W_BY_HEIGHT =
  (ROLLSIGN_STRIP_H - NEXT_EDGE_GUTTER - NEXT_BOX_BOTTOM - NEXT_BOX_H * Math.cos(rad(NEXT_ANGLE))) /
  Math.sin(rad(NEXT_ANGLE))
export const NEXT_BOX_W = Math.floor(Math.min(NEXT_BOX_W_BY_WIDTH, NEXT_BOX_W_BY_HEIGHT))
/** The name's room: the bar less its border and padding on both sides. */
export const NEXT_BOX_INNER_W = NEXT_BOX_W - 2 * (NEXT_BOX_BORDER + NEXT_BOX_PAD_X)

/** Perpendicular air kept between the bar's lower edge and column 2's stop name. */
const NEXT_COL_CLEARANCE = 31
/**
 * The callout's own column, wider than an upcoming stop's. The bar overhangs it by design
 * and lies over the columns behind — safe only because bar and labels are the SAME diagonal,
 * so they are parallel lines that never converge. What this width actually buys is the
 * clearance above, measured at column 2's label pivot; narrowing it walks that stop's name
 * up into the bar. It tracks `NEXT_BOX_LEFT` one-for-one, which is why it is derived from it.
 */
export const NEXT_COL_W = Math.ceil(
  (NEXT_COL_CLEARANCE +
    (ROLLSIGN_STRIP_H - ROLLSIGN_LABEL_TOP - NEXT_BOX_BOTTOM) * Math.cos(rad(NEXT_ANGLE))) /
    Math.sin(rad(NEXT_ANGLE)) -
    ROLLSIGN_LABEL_LEFT +
    NEXT_BOX_LEFT,
)

// ---------------------------------------------------------------------------
// How big a stop name is set
// ---------------------------------------------------------------------------
// As large as it fits and no larger — a sign is read across a car, so the cap is a budget
// question, not a taste one. It sits under BOTH sections above because two boxes have a say
// and the smaller wins; the strip and the callout then share one size, as the sign does.
//
// Across the strip: labels are parallel lines ROLLSIGN_COL_W · sin(angle) ≈ 55px apart, and
// a label is a name over a transfer row. Whatever that block doesn't spend is the clear air
// between one stop's transfers and the next stop's name — hence the clearance term.
//
// Inside the callout: the red bar is a FIXED frame (its 4.5:1 comes off the photograph, and
// an empty tail is the format), so the same two rows have to sit inside NEXT_BOX_H's border.
//
// The strip allows 22px and the bar 23, so 22 it is — up from the 20 that was written down
// by hand, and the strip is what binds. To go higher, buy the room first: a wider column or
// a steeper angle (a taller bar alone would do nothing) — and neither can be raised on its
// own, because each of those numbers is already holding something else up.
const LABEL_LINE_HEIGHT = 1.2
/** The transfer row under a name, in the strip and in the callout alike. */
export const ROLLSIGN_TRANSFER_PX = 15
const LABEL_ROW_GAP = 2
/** Clear air kept between one stop's transfer row and the next stop's name. */
const LABEL_NEIGHBOUR_CLEARANCE = 8
/** The transfer row's full cost to a label block: the row, its line-height and the gap. */
const TRANSFER_ROW_H = LABEL_ROW_GAP + ROLLSIGN_TRANSFER_PX * LABEL_LINE_HEIGHT

/** The largest name that leaves room for a transfer row under it inside `boxH`. */
function nameBudgetPx(boxH: number): number {
  return Math.floor((boxH - TRANSFER_ROW_H) / LABEL_LINE_HEIGHT)
}

const NAME_MAX_PX = Math.min(
  nameBudgetPx(
    ROLLSIGN_COL_W * Math.sin((ROLLSIGN_LABEL_ANGLE * Math.PI) / 180) -
      LABEL_NEIGHBOUR_CLEARANCE,
  ),
  nameBudgetPx(NEXT_BOX_H - 2 * NEXT_BOX_BORDER),
)
// 12px is the app's readability floor (see the `Type` scale). A name long enough to hit it
// is set at 12 and allowed to reach into the gutter rather than shrinking out of legibility.
const NAME_MIN_PX = 12
/** The labels' letter-spacing, which `measureText` knows nothing about. */
const LABEL_TRACKING_PX = 1

/**
 * A label block at full size: a name at the cap, the gap under it and a transfer row. The
 * tallest a label gets, and so the one to check clearances against.
 */
export const ROLLSIGN_LABEL_BLOCK_H = Math.round(
  NAME_MAX_PX * LABEL_LINE_HEIGHT + TRANSFER_ROW_H,
)

/**
 * The size one stop name is set at: the cap unless that would run it off the top of the
 * strip, and as large as fits when it would. Short names — most of them — are untouched, so
 * the strip still reads as one size and only the "Sutphin Blvd-Archer Av-JFK Airport"
 * outliers step down. `runPx` defaults to the strip's diagonal budget; the next-stop callout
 * passes the inside of its red box instead, where the limit is the box and not the strip.
 */
export function rollsignNamePx(name: string, runPx = ROLLSIGN_LABEL_RUN_PX): number {
  return fitFontPx(
    name,
    LED_FONT,
    400,
    runPx - LABEL_TRACKING_PX * name.length,
    NAME_MAX_PX,
    NAME_MIN_PX,
  )
}

// ---------------------------------------------------------------------------
// The destination headline
// ---------------------------------------------------------------------------
// The panel's top line is "TO <terminus>", and the terminus is a GTFS station name — the
// station's whole legal name, cross street and all ("Coney Island-Stillwell Av",
// "Van Cortlandt Park-242 St"). Set at 28px in the LED face, sharing the row with a route
// bullet and two buttons, that overruns the row on a phone and the ellipsis eats the end of
// the name: the F read "TO CONEY ISLAND-STILLW…", which names nowhere.
//
// So the headline shrinks before it clips, and drops the cross street only when shrinking
// has run out. MTA's own signs print the place rather than the cross street — the F reads
// TO CONEY ISLAND — and the leading component of a station name *is* that place.

/** A leading component shorter than this is a fragment, not a place; keep the whole name. */
const SHORT_DEST_MIN_CHARS = 4

/**
 * A station name reduced to the place it names: everything before the first hyphen.
 * "Coney Island-Stillwell Av" → "Coney Island", "Flushing-Main St" → "Flushing". Names
 * without a hyphen, and the few whose head is too short to stand alone, come back whole.
 */
export function shortDestination(name: string): string {
  const head = name.split('-')[0].trim()
  return head.length >= SHORT_DEST_MIN_CHARS ? head : name.trim()
}

export interface HeadlineFit {
  /** What to print — already upper-cased, since a sign has no lower case. */
  text: string
  /** The size it fits at. */
  px: number
}

export interface HeadlineFitOptions {
  font: string
  weight: number
  maxPx: number
  /**
   * Where the two rungs of the ladder meet: the size below which the full name is traded for
   * the short one instead of being set smaller still. NOT `fitFontPx`'s floor.
   */
  minPx: number
  /** Letter-spacing, whichever way the row happens to declare it (the LCD is in em, the
   * rollsign in a fixed px that does not scale with the shrinking type). */
  trackingEm?: number
  trackingPx?: number
}

/**
 * Where this vehicle is going, at the largest size that fits `availablePx` — full name if it
 * can be read at `minPx` or better, otherwise `shortDestination`. Only the second rung can
 * clip, and only for a name with no hyphen to cut at.
 */
export function signHeadline(
  terminus: string,
  availablePx: number,
  opts: HeadlineFitOptions,
): HeadlineFit {
  const { font, weight, maxPx, minPx, trackingEm = 0, trackingPx = 0 } = opts
  const fit = (text: string, floorPx: number) =>
    fitFontPx(
      text,
      font,
      weight,
      availablePx - trackingPx * text.length,
      maxPx,
      floorPx,
      trackingEm,
    )

  const full = `TO ${terminus.toUpperCase()}`
  // Floored at 1 rather than at minPx, because `fitFontPx` clamps: a size that came back as
  // minPx cannot be told apart from one that fits there, and "does it fit?" is the question.
  const fullPx = fit(full, 1)
  if (fullPx >= minPx) return { text: full, px: fullPx }

  const short = `TO ${shortDestination(terminus).toUpperCase()}`
  // Nothing to cut: set it at the floor and let the ellipsis do what it has always done.
  if (short === full) return { text: full, px: minPx }
  return { text: short, px: fit(short, minPx) }
}

// The three fitting helpers that used to live here — `signServicePx`, `signDestinationPx`,
// `signBulletPx` — are gone with the column they existed for. Each of them was arithmetic for
// surviving 104px: a name fitted to its longest WORD because it had to wrap, a destination
// pegged to a ratio of that, a bullet sized as a share of the width. A full-width band needs
// none of it, and the sizes are three plain constants up beside `STRIP_MIN_W`.
//
// ⚠️ One thing from that work still applies wherever this panel sets type: `public/fonts/`
// bundles ONE cut of TeX Gyre Heros (700), and CSS matches the family before the weight, so
// every size here renders at 700 however it asks. Size and colour are the whole hierarchy; a
// `font-weight: 500` in the LCD face is a no-op that reads as an intention.

export function fmtTime(epoch: number | null): string {
  if (!epoch) return '--'
  return formatAbsoluteClock(epoch)
}

/** The clock time without the leading zero — how a sign prints a time it isn't shouting. */
export function fmtShort(epoch: number | null): string {
  if (!epoch) return '--'
  return formatShortClock(epoch)
}

/** Minutes out, as a sign would print it. Shared by both faces of the stop list. */
export function fmtCountdown(epoch: number | null): string {
  if (!epoch) return ''
  return formatTransitCountdown(epoch, { nowLabel: 'NOW', pastLabel: 'NOW', unit: 'MIN' })
}

/**
 * The sign the riding panel is imitating, resolved from what the rider actually boarded.
 * A subway car has the amber dot-matrix rollsign; a bus has the backlit LCD over the
 * farebox. Same information, two physical devices, so the panel's shared rows (route +
 * destination headline, the alight countdown, the mini line) take their face, ink and wash
 * from here instead of branching on the vehicle type at every one of them.
 */
export interface SignSkin {
  /** The face the whole panel is set in. */
  font: string
  /** Wash laid over the panel: CRT scanlines on the rollsign, nothing on an LCD. */
  wash: string
  /** The sign's own voice — where this vehicle is going. */
  head: string
  /** Confirmations and times: there is still time to sit down. */
  calm: string
  /** The one line with a deadline. */
  urgent: string
  /** Secondary marks — a clock time already passed, a tap hint. */
  dim: string
  /**
   * Lit type. The rollsign blooms because phosphor does; an LCD is a flat backlight, so on
   * the bus this is the color and nothing else. `size`/`alpha` scale the bloom with the type
   * behind it and are ignored on the LCD.
   */
  ink: (color: string, size?: number, alpha?: number) => string
}

export function signSkin(type: VehicleType | undefined): SignSkin {
  if (type === 'bus') {
    return {
      font: LCD_FONT,
      wash: '',
      head: LCD_INK,
      calm: LCD_GREEN,
      urgent: LCD_RED,
      dim: LCD_DIM,
      ink: (color) => `color: ${color};`,
    }
  }
  return {
    font: LED_FONT,
    wash: LED_SCANLINES,
    head: LED_AMBER,
    calm: LED_GREEN,
    urgent: LED_RED,
    dim: LED_RAIL,
    ink: (color, size, alpha) => ledGlow(color, size, alpha),
  }
}
